import DefaultButton from '@/components/DefaultButton'
import type { PlanTierDetail } from './planDetail'
import React, { ReactNode, useCallback, useEffect, useRef } from 'react'
import { styled } from 'baseui'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import { customAmplitude } from '@/utils/customAmplitude'
import { KIND } from 'baseui/button'

import { lightTheme, superBrandGradient } from '@/customTheme'
import { ShowModalType } from '@/scenes/engine/common/constants'
import useModal from '@/hooks/useModal'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { useSelector } from 'react-redux'
import { selectOpenModalTryPremium, selectUser } from '@/store/slices/user/selectors'
import { useAppDispatch } from '@/store/store'
import { useLocalizedT } from '@/hooks/useLocalizedT'
import AVATAR_1 from './images/avatar_1.png'
import AVATAR_2 from './images/avatar_2.png'
import AVATAR_3 from './images/avatar_3.png'
import Arwards from '@assets/images/awards.png'
import { FALLBACK_PLAN_DETAIL, FALLBACK_SUPER_PLAN_DETAIL, formatPlanPrice } from './fallbackPlanDetail'

export type PlanType = 'premium' | 'super'

const PlanCard = styled('div', ({ $inline, $yearly }: { $inline: boolean; $yearly?: boolean }) => ({
  height: '81px',
  padding: $yearly ? '0 22px' : '0 23px',
  ':focus-visible': { outline: `2px solid ${lightTheme.colors.primary}`, outlineOffset: '2px' },
  ...($inline ? {
    '@media screen and (max-width: 480px)': {
      height: 'auto', minHeight: '81px', padding: '16px', flexWrap: 'wrap', gap: '8px',
    },
  } : {}),
}))

interface MetsikonChoosePlanProps {
  onConfirmPlan: (plan: number) => void
  planDetail: PlanTierDetail | null
  /**
   * Monthly price × 12, for the yearly strike-through. Omitted embeds
   * (partner pages) derive it from the plan's monthly amount.
   */
  yearlyFromMonthlyAmount?: number
  planActive: number
  setPlanActive: (plan: number) => void
  planType: PlanType
  /**
   * `modal` is the paywall's left pane and owns half the dialog width.
   * `inline` fills its container instead, for embedding the same panel in a
   * page (partner landing pages).
   */
  layout?: 'modal' | 'inline'
  /** Rendered between the plan cards and the CTA — partner pages put a coupon field here. */
  couponSlot?: ReactNode
  /** Renders labels in an explicit locale without changing the app language. */
  locale?: string
  /**
   * Embedded copies sit outside the paywall funnel, so they opt out rather than
   * inflating `Premium Choose Plan` with page views that never opened a modal.
   */
  trackChoosePlanEvent?: boolean
}

type PlanTypeControl =
  | { showPlanTypeToggle?: true; setPlanType: (type: PlanType) => void }
  | { showPlanTypeToggle: false; setPlanType?: never }

function MetsikonChoosePlan({
  onConfirmPlan,
  planDetail,
  yearlyFromMonthlyAmount,
  planActive,
  setPlanActive,
  planType,
  setPlanType,
  layout = 'modal',
  showPlanTypeToggle = true,
  couponSlot,
  locale,
  trackChoosePlanEvent = true,
}: MetsikonChoosePlanProps & PlanTypeControl) {
  const t = useLocalizedT(locale)
  const monthlyRef = useRef<HTMLDivElement>(null)
  const yearlyRef = useRef<HTMLDivElement>(null)
  const handlePlanKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, plan: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setPlanActive(plan)
    } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault()
      setPlanActive(plan === 1 ? 2 : 1)
      ;(plan === 1 ? yearlyRef : monthlyRef).current?.focus()
    }
  }
  const { setShowModalType } = useModal()
  const openModalTryPremium = useSelector(selectOpenModalTryPremium)
  const dispatch = useAppDispatch()
  const user = useSelector(selectUser)

  // Fall back to the whole tier-matching plan object (not field-by-field) so
  // the display string and the numeric amount always come from the *same*
  // source. A per-field `??` fallback could pair a real Paddle `price` with a
  // fallback `priceAmount` (or mix Premium/Super), reintroducing the
  // display/amount mismatch this guards against.
  const tierFallback = planType === 'super' ? FALLBACK_SUPER_PLAN_DETAIL : FALLBACK_PLAN_DETAIL
  const effectivePlanDetail = planDetail ?? tierFallback
  const monthlyPlan = {
    price: effectivePlanDetail.month?.price,
    canFreeTrial: effectivePlanDetail.month?.canFreeTrial,
    trialDays: effectivePlanDetail.month?.trialDays ?? 0,
  }
  const yearlyPlan = {
    price: effectivePlanDetail.year?.price,
    canFreeTrial: effectivePlanDetail.year?.canFreeTrial,
    trialDays: effectivePlanDetail.year?.trialDays ?? 0,
  }
  // Derive arithmetic from the numeric amount Paddle exposes via PricePreview
  // (`totals.total` in minor units, normalized to a decimal in SignInManager).
  // Parsing the localized display string (`11,99 €`) for math is unsafe — comma
  // decimals get stripped and inflate the value 100×.
  const currencyCode = effectivePlanDetail.currencyCode
  const countryCode = effectivePlanDetail.countryCode
  const monthlyAmount = effectivePlanDetail.month?.priceAmount
  const yearlyAmount = effectivePlanDetail.year?.priceAmount

  const yearlyFromMonthPrice = formatPlanPrice(
    yearlyFromMonthlyAmount ?? monthlyAmount * 12,
    currencyCode,
    countryCode,
  )
  const yearlyPricePerMonth = formatPlanPrice(yearlyAmount / 12, currencyCode, countryCode)

  const isPremium = planType === 'premium'
  const accentColor = isPremium ? '#ff0560' : '#9d01f4'
  const isInline = layout === 'inline'
  const isSuperUpgradePath =
    planType === 'super' && Boolean(user?.userIsPremium) && !Boolean(user?.userIsSuper)

  const canFreeTrial = planActive === 1 ? planDetail?.month?.canFreeTrial : planDetail?.year?.canFreeTrial
  const trialDays = planActive === 1 ? (planDetail?.month?.trialDays ?? 0) : (planDetail?.year?.trialDays ?? 0)

  const ctaLabel = isPremium && canFreeTrial
    ? t('Try free for %d days', { arg: trialDays })
    : isSuperUpgradePath
      ? t('Subscribe now')
      : t('Continue')

  const handleSignInClick = useCallback(() => {
    setShowModalType(ShowModalType.SIGN_IN)
    dispatch(
      setOpenModalTryPremium({
        ...openModalTryPremium,
        isOpen: false,
      })
    )
  }, [dispatch, openModalTryPremium, setShowModalType])

  useEffect(() => {
    if (!trackChoosePlanEvent) return
    customAmplitude('Premium Choose Plan')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div style={{
      position: 'relative',
      padding: isInline
        ? `24px 28px ${user ? 28 : 20}px 28px`
        : `48px 48px ${user ? 48 : 24}px 48px`,
      display: 'flex',
      flexDirection: 'column',
      width: isInline ? '100%' : '50%',
      // Logical value so an RTL host page mirrors the panel instead of
      // stranding every label on the wrong edge.
      textAlign: 'start',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {/* Title */}
      <h2 style={{ ...lightTheme.typography.Header26bold, margin: 0 }}>
        {isPremium ? t('Bazaart Premium') : t('Bazaart Super')}
      </h2>
      <p style={{ ...lightTheme.typography.Header16bold, marginTop: '4px', marginBottom: '32px' }}>
        {isPremium
          ? t('Perfect for individuals and solopreneurs.')
          : t('Best for growing online sellers and brands.')}
      </p>

      {/* Premium / Super toggle pill */}
      {showPlanTypeToggle && !user?.userIsPremium && <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
        <div style={{
          position: 'relative',
          width: '280px',
          height: '48px',
          borderRadius: '100px',
          backgroundColor: isPremium ? 'rgba(255, 5, 96, 0.1)' : '#eecff6',
        }}>
          <div style={{
            position: 'absolute',
            top: '6px',
            bottom: '6px',
            left: isPremium ? '6px' : '140px',
            width: '134px',
            borderRadius: '100px',
            backgroundColor: accentColor,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
            transition: 'left 0.2s ease',
          }} />
          <div
            onClick={() => setPlanType?.('premium')}
            style={{
              position: 'absolute',
              top: 0, left: 0,
              width: '140px', height: '48px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1, cursor: 'pointer', userSelect: 'none',
              fontSize: '14px', fontWeight: 500, lineHeight: '24px',
              color: isPremium ? '#fff' : lightTheme.colors.blackGray,
            }}
          >
            {t('Premium')}
          </div>
          <div
            onClick={() => {
              customAmplitude('Click', { Source: 'super.tab.paywall' })
              setPlanType?.('super')
            }}
            style={{
              position: 'absolute',
              top: 0, right: 0,
              width: '140px', height: '48px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1, cursor: 'pointer', userSelect: 'none',
              fontSize: '14px', fontWeight: 500, lineHeight: '24px',
              color: isPremium ? lightTheme.colors.blackGray : '#fff',
            }}
          >
            {t('Super')}
          </div>
        </div>
      </div>}

      {/* Plan cards */}
      <div role="radiogroup" aria-label={t('Choose a plan')} style={{ display: 'flex', flexDirection: 'column', gap: '24px'}}>
        {/* Monthly */}
        <PlanCard
          $inline={isInline} ref={monthlyRef} role="radio" aria-label={t('Monthly')} aria-checked={planActive === 1}
          tabIndex={planActive === 1 ? 0 : -1} onKeyDown={event => handlePlanKeyDown(event, 1)}
          onClick={() => setPlanActive(1)}
          style={{
            backgroundColor: planActive === 1 ? (isPremium ? '#fff7fa' : '#faf2fe') : '#fff',
            border: planActive === 1 ? `2px solid ${accentColor}` : '1px solid #e5e5e5',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <div>
            <p style={{ ...lightTheme.typography.Small16Bold, margin: 0 }}>{t('Monthly')}</p>
            <p style={{ ...lightTheme.typography.Small12medium, margin: 0, color: lightTheme.colors.blackGray }}>
              {t('%@ billed monthly', { arg: monthlyPlan.price })}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', ...(isInline ? { marginInlineStart: 'auto' } : {}) }}>
            <span style={{ ...lightTheme.typography.Small16Bold }}>{monthlyPlan.price}</span>
            <span style={{ ...lightTheme.typography.Small12regular }}>/</span>
            <span style={{ ...lightTheme.typography.Small12regular }}>{t('month')}</span>
          </div>
        </PlanCard>

        {/* Yearly */}
        <PlanCard
          $inline={isInline} $yearly ref={yearlyRef} role="radio" aria-label={t('Yearly')} aria-checked={planActive === 2}
          tabIndex={planActive === 2 ? 0 : -1} onKeyDown={event => handlePlanKeyDown(event, 2)}
          onClick={() => setPlanActive(2)}
          style={{
            backgroundColor: planActive === 2 ? (isPremium ? '#fff7fa' : '#faf2fe') : '#fff',
            border: planActive === 2 ? `2px solid ${accentColor}` : '1px solid #e5e5e5',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ ...lightTheme.typography.Small16Bold }}>{t('Yearly')}</span>
              <div style={{
                backgroundColor: isPremium ? 'rgba(255, 5, 96, 0.1)' : 'rgba(157, 1, 244, 0.1)',
                borderRadius: '100px',
                padding: '0 10px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
              }}>
                <span style={{
                  ...lightTheme.typography.Small11medium,
                  color: accentColor,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                }}>
                  {isPremium && yearlyPlan.canFreeTrial
                    ? t('Free %d-day trial', { arg: yearlyPlan.trialDays })
                    : t('Best Value')}
                </span>
              </div>
            </div>
            <p style={{ ...lightTheme.typography.Small12medium, margin: 0 }}>
              {/* Isolated so an RTL host page doesn't merge this price with the
                  one inside the sentence below into a single left-to-right run,
                  which reverses which of the two reads as "before discount". */}
              <span style={{
                color: lightTheme.colors.grayScale400,
                textDecoration: 'line-through',
                unicodeBidi: 'isolate',
              }}>{yearlyFromMonthPrice}</span>
              {' '}{t('%@ billed yearly', { arg: yearlyPlan.price })}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', ...(isInline ? { marginInlineStart: 'auto' } : {}) }}>
            <span style={{ ...lightTheme.typography.Small16Bold }}>{yearlyPricePerMonth}</span>
            <span style={{ ...lightTheme.typography.Small12regular }}>/</span>
            <span style={{ ...lightTheme.typography.Small12regular }}>{t('month')}</span>
          </div>
        </PlanCard>
      </div>

      {couponSlot ? <div style={{ marginTop: '24px' }}>{couponSlot}</div> : null}

      {/* Social proof. Modal only: a landing page states its own, in the partner's
          voice and aimed at their audience, a few hundred pixels away — and the
          ~80px this costs is what pushes the page's gallery below the fold. */}
      {!isInline && <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '24px',
        // Fills the modal's leftover column height.
        marginTop: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src={AVATAR_3} alt="" style={{ width: 32, height: 32, borderRadius: '50%', position: 'relative', zIndex: 3 }} />
          <img src={AVATAR_2} alt="" style={{ width: 32, height: 32, borderRadius: '50%', marginLeft: '-8px', position: 'relative', zIndex: 2 }} />
          <img src={AVATAR_1} alt="" style={{ width: 32, height: 32, borderRadius: '50%', marginLeft: '-8px', position: 'relative', zIndex: 1 }} />
        </div>
        <div style={{
          backgroundColor: lightTheme.colors.grayScale100,
          borderRadius: '100px',
          padding: '4px 16px',
          marginLeft: '-12px',
        }}>
          <span style={{ fontSize: '13px', fontWeight: 500, lineHeight: '24px', color: lightTheme.colors.blackGray }}>
            {t('Trusted by 1M+ creators and businesses')}
          </span>
        </div>
      </div>}

      {isInline ? (
        <div style={{ marginTop: '24px' }}>
          <DefaultButton type="primary-gradient" size="large" fullWidth
            label={ctaLabel} disabled={!planDetail} onClick={() => onConfirmPlan(planActive)} />
        </div>
      ) : (
        <ButtonCustom type={SizeButton.LARGE} kind={KIND.primary} disabled={!planDetail}
          style={{ position: 'relative', marginBottom: 0, background: isPremium ? lightTheme.colors.pink : superBrandGradient }}
          onClick={() => onConfirmPlan(planActive)}>
          {ctaLabel}
        </ButtonCustom>
      )}

      {!user &&
        (isInline ? (
          /* Embedded on a partner page this is the only other action on the
             surface, so "Sign in" is a real link — brand-coloured, with hover and
             active states, and only the link itself clickable. The modal keeps the
             original whole-line treatment below. */
          <p
            style={{
              marginBottom: 0,
              marginTop: '8px',
              ...lightTheme.typography.Small12medium,
              color: lightTheme.colors.grayScale400,
              textAlign: 'center',
            }}
          >
            <span style={{ fontWeight: '400' }}>{t('Already Premium')}?</span>{' '}
            <DefaultButton type="text" label={t('Sign in')} onClick={handleSignInClick} />
          </p>
        ) : (
          <h4
            style={{
              marginBottom: 0,
              marginTop: '8px',
              ...lightTheme.typography.Small12medium,
              padding: '0 16px',
              color: 'rgba(153, 153, 153, 1)',
              cursor: 'pointer',
              textAlign: 'center',
            }}
            onClick={handleSignInClick}
          >
            <span style={{ fontWeight: '400' }}>{t('Already Premium')}?</span> {t('Sign in')}
          </h4>
        ))}
    </div>
  )
}

export default MetsikonChoosePlan
